import React, { useState, useEffect } from "react";
import { Menu } from "antd";
import { MDSConfigProvider } from "@mds/merck-design-system-dev";
import {
  HomeOutlined,
  LoginOutlined,
  AppstoreAddOutlined,
  UserOutlined,
  LogoutOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import { menuApi } from "../services/topmenuService";
import Loading from "react-fullscreen-loading";

interface MenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  href: string;
}

export default function TopMenu() {
  const [current, setCurrent] = useState<string>("home");
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(false);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        setShowLoader(true);
        const isLoggedIn = sessionStorage.getItem("loggedin") === "Y";
        const { menuResponse } = await menuApi();

        if (Array.isArray(menuResponse)) {
          const filteredItems = menuResponse.filter((item) => {
            if (item.MENU_NAME === "Login" && isLoggedIn) return false;
            if (item.MENU_NAME === "Dashboard" && !isLoggedIn) return false;
            return true;
          });

          const generatedItems: MenuItem[] = filteredItems.map((item) => {
            let icon;
            switch (item.MENU_NAME) {
              case "Home":
                icon = <HomeOutlined />;
                break;
              case "Dashboard":
                icon = <DashboardOutlined />;
                break;
              case "Login":
                icon = <LoginOutlined />;
                break;
              case "About":
                icon = <AppstoreAddOutlined />;
                break;
              default:
                icon = <UserOutlined />;
            }

            return {
              key: item.MENU_NAME.toLowerCase(),
              label: item.MENU_NAME,
              icon: icon,
              href: item.URL_VALUE || "/",
            };
          });

          if (isLoggedIn) {
            generatedItems.push({
              key: "logout",
              label: "Logout",
              icon: <LogoutOutlined />,
              href: "",
            });
          }

          setMenuItems(generatedItems);

          // Set selected menu based on URL
          const currentPath = window.location.pathname.replace(/\/$/, "");
          const matchedItem = generatedItems.find(
            (item) => item.href.replace(/\/$/, "") === currentPath
          );
          setCurrent(matchedItem?.key || "home");
        } else {
          alert("Invalid menu format.");
        }
      } catch (error) {
        console.error("Error during menu fetch:", error);
      } finally {
        setShowLoader(false);
      }
    };

    const delay = setTimeout(fetchMenuItems, 100);
    return () => clearTimeout(delay);
  }, []);

  useEffect(() => {
    localStorage.setItem("currentMenu", current);
  }, [current]);

  const handleClick = (e: { key: string }) => {
    setCurrent(e.key);
    const selectedItem = menuItems.find((item) => item.key === e.key);

    if (!selectedItem) return;

    if (selectedItem.label === "Login") {
      sessionStorage.clear();
      window.location.href = selectedItem.href;
    } else if (selectedItem.label === "Logout") {
      sessionStorage.clear();
      window.location.href = "login.html";
    } else {
      window.location.href = selectedItem.href;
    }
  };

  return (
    <MDSConfigProvider theme="companyLightTheme">
      <Menu
        theme="dark"
        mode="horizontal"
        selectedKeys={[current]}
        onClick={handleClick}
        className="menu"
      >
        {menuItems.map((item) => (
          <Menu.Item
            key={item.key}
            icon={item.icon}
            className={item.label === "Logout" ? "logout" : undefined}
          >
            {item.label}
          </Menu.Item>
        ))}
      </Menu>
      {showLoader && (
        <Loading loading background="rgba(0,0,0,.5)" loaderColor="#3498db" />
      )}
    </MDSConfigProvider>
  );
}
